import { SearchActions } from "~/types/actions.enum";
import { CompanyFilter, NormalizedCompany } from "~/types/search.types";

// ✅ Initial states
export const initialState: TCompanyState = {
    companies: [],
    filters: [],
    selectedCompanyIds: [],
    totalSelected: 0
};

// State type defns
export type TCompanyState = {
    companies: NormalizedCompany[];
    filters: CompanyFilter[];
    selectedCompanyIds: number[];
    totalSelected: number;
};

// Action type defn
export type TCompanyAction =
    | {
          type: SearchActions.SET_COMPANY_ITEMS;
          payload: NormalizedCompany[];
      }
    | {
          type: SearchActions.SET_COMPANY_FILTERS;
          payload: CompanyFilter[];
      }
    | {
          type: SearchActions.SET_SELECTED_COMPANY;
          payload: number;
      }
    | {
          type: SearchActions.REMOVE_SELECTED_COMPANY;
          payload: number;
      }
    | {
          type: SearchActions.RESET_SELECTION;
      };

// Store type
export type TCompanyMethods =
    | "dispatch"
    | "getSelectedCompanies"
    | "getCompaniesByDisclosure"
    | "getCompanyById"
    | "isCompanySelected"
    | "reset";

export type TCompanyStore = TCompanyState & {
    /**
     * Sets the Company state using a reducer pattern.
     * @param {TCompanyAction} action - `TCompanyAction` action to apply.
     * @example
     * ```
     * const dispatch = useCompanyStore((state) => state.dispatch)
     * dispatch({ type: SearchActions.SET_COMPANY_ITEMS, payload: companyList })
     * ```
     */
    dispatch: (action: TCompanyAction) => void;
    /**
     * Retrieves all selected Company object items whose ID is in the `selectedCompanyIds[]` array.
     */
    getSelectedCompanies: () => NormalizedCompany[];
    /**
     * Retrieves a list of Company items by `disclosureId`.
     * @param disclosureId - A disclosure ID
     * @example const filtered = getCompaniesByDisclosure(disclosureId);
     */
    getCompaniesByDisclosure: (disclosureId: number) => CompanyFilter[] | undefined;
    /**
     * Retrieves a Company Item by its ID
     * @param id Company ID
     */
    getCompanyById: (id: number) => NormalizedCompany | undefined;
    /**
     * Checks if a company is selected
     * @param id - Company ID
     */
    isCompanySelected: (id: number) => boolean;
    /**
     * Resets the store to its initial state
     */
    reset: () => void;
};
